import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { Col, Row, Modal, ModalHeader, ModalBody, Alert, Button, Nav, NavItem, NavLink, TabContent, TabPane, Card, CardBody } from "reactstrap";
import { toast } from "react-toastify";
import ComplianceMonitoringService from "src/modules/3rd-party-management/apis/admin/compliance/monitoring/MonitoringService";
import AIRiskAssessmentModal from "src/modules/3rd-party-management/components/RisksAndTasks/risks/ai/AssessmentModal";
import {
    CHAT_BOT_DOMAIN,
    API_BASE_URL
} from 'src/modules/3rd-party-management/constants';
import ComplianceScreeningService from "src/modules/3rd-party-management/apis/admin/compliance/screening/ScreeningService";
import AIGetHelpIcon from "src/modules/3rd-party-management/assets/images/microchip/white.png";
import AIBotty from "src/modules/3rd-party-management/assets/images/bottyAi-2.png";
import { RiInformationLine, RiThumbDownLine, RiThumbUpLine } from "react-icons/ri";
import GeneralInfosTable from "src/modules/3rd-party-management/pages/admin/compliance/components/profile-detail/GeneralInfosTable";
import RiskAccrodions from "src/modules/3rd-party-management/pages/admin/compliance/components/profile-detail/RiskAccrodions";
import DeletionStatus from "src/modules/3rd-party-management/pages/admin/compliance/components/profile-detail/DeletionStatus";
import MatchMonitoringResultHeader from "./MatchMonitoringResultHeader";
import MatchMonitoringResultDetails from "./MatchMonitoringResultDetails";
import { EMPTY_LIST } from "src/common/constants";

import './styles.scss';

const MonitoringBody = ({ 
    t, 
    fullProfileDetailQuery, 
    setAiBasedRiskAssessmentModalStatus, 
    monitoringDetailQuery 
}) => {
    const [activeTab, setActiveTab] = useState("match");

    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    const {
        data: resultData
    } = fullProfileDetailQuery;

    return (
        <Row className="d-flex flex-lg-row-reverse">
            <Col sm="12" md="12" lg="4" xl="3" className="d-none d-lg-block">
                <div className="side-box ai-help-box mb-4">
                    <h5>
                        {t('Risk Assessment Support by AI')}
                    </h5>

                    <div className="image-box">
                        <img src={AIBotty} alt="Risk Assessment Support by AI" />
                    </div>

                    <Button onClick={() => {
                        setAiBasedRiskAssessmentModalStatus(true)
                    }} className="d-flex justify-content-center w-100" color="primary">
                        <img className="me-2" src={AIGetHelpIcon} alt="" />
                        {t('Get Help From AI')}
                    </Button>
                </div>
            </Col>

            <Col sm="12" md="12" lg="8" xl="9">
                <div className="finding-content">
                    <DeletionStatus 
                        t={t}
                        isDeleted={resultData?.isDeleted}
                        deletionReason={resultData?.deletionReason}
                    />

                    <div className="alert alert-info d-flex justify-content-start align-items-center gap-2">
                        <RiInformationLine size="32" />
                        <div>
                            <b>{t('Please verify results')}</b>
                            <p className="mb-0">
                                {t('Results may be affected by factors such as similar names.')}<br />
                                {t('Please review the output and provide feedback on its relevance:')}<br />
                                <RiThumbUpLine size="20" /> {t('Thumbs Up: Relevant')} <br />
                                <RiThumbDownLine size="20" /> {t('Thumbs Down: False Positive')}
                            </p>
                        </div>
                    </div>

                    <MatchMonitoringResultHeader
                        monitoringDetailQuery={monitoringDetailQuery}
                    />

                    <Card>
                        <CardBody>
                            <Nav tabs>
                                {/* <NavItem>
                                    <NavLink
                                        active={activeTab === "profile"}
                                        onClick={() => toggleTab("profile")}
                                    >
                                        {t('Profile')}
                                    </NavLink>
                                </NavItem> */}

                                <NavItem>
                                    <NavLink 
                                        active={activeTab === "match"} 
                                        onClick={() => toggleTab("match")}
                                    >
                                        {t('Match Details')}
                                    </NavLink>
                                </NavItem>

                                <NavItem>
                                    <NavLink 
                                        active={activeTab === "reviews"} 
                                        onClick={() => toggleTab("reviews")}
                                    >
                                        {t('Reviews')}
                                    </NavLink>
                                </NavItem>
                            </Nav>

                            <TabContent activeTab={activeTab} className="mt-3">
                                {/* <TabPane tabId="profile">
                                    <GeneralInfosTable
                                        resultData={resultData}
                                        complianceType="monitoring"
                                    />

                                    <RiskAccrodions
                                        data={resultData}
                                    />
                                </TabPane> */}

                                <TabPane tabId="match">
                                    <MatchMonitoringResultDetails
                                        monitoringDetailQuery={monitoringDetailQuery}
                                    />
                                </TabPane>

                                <TabPane tabId="reviews">
                                    <p>
                                        {t(EMPTY_LIST)}
                                    </p>
                                </TabPane>
                            </TabContent>
                        </CardBody>
                    </Card>
                </div>
            </Col>
        </Row>
    )
}

const MonitoringAlertBody = function (props) {
    const {
        organization,
        t,
        id,
        supplierId,
        type
    } = props;

    const history = useHistory();

    const [resultFullProfileId, setResultFullProfileId] = useState();
    const [aiBasedRiskAssessmentModalStatus, setAiBasedRiskAssessmentModalStatus] = useState(false);

    const resultDetailQuery = useQuery({
        queryKey: [
            '3rd-party-management-fetch-compliance-monitoring-result-details',
            type,
            id
        ],
        queryFn: async () => {
            const service = ComplianceMonitoringService.getInstance();

            return await service.fetchAlertDetails(
                id
            );
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('Failed to load profile details. Please try again.'), {
                type: 'error'
            });
        },
    });

    const fullProfileDetailQuery = useQuery({
        queryKey: [
            '3rd-party-management-fetch-compliance-monitoring-result-details',
            type,
            resultFullProfileId
        ],
        queryFn: async () => {
            const service = ComplianceScreeningService.getInstance();

            if (type === 'business') {
                return await service.fetchBusinessScreeningResultItemDetails(
                    resultFullProfileId
                );
            }

            return await service.fetchPersonScreeningResultItemDetails(
                resultFullProfileId
            );
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        // enabled: !!resultFullProfileId,
        enabled: false,
        onError: () => {
            toast(t('Failed to load profile details. Please try again.'), {
                type: 'error'
            });
        }
    });

    useEffect(() => {
        if (resultDetailQuery.data?.type) {
            if (resultDetailQuery.data.type === 'business') {
                setResultFullProfileId(resultDetailQuery.data.business.business_id);
            }
            else if (resultDetailQuery.data.type === 'person'){
                setResultFullProfileId(resultDetailQuery.data.person.person_id);
            }
        }
    }, [ resultDetailQuery.data ]);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    const renderLoadingPlaceholder = () => {
        return (
            <Row>
                <Col sm="12" md="9">
                    <div className="dt-field dt-skeleton mb-4" style={{ width: '70%' }}></div>
                    <div className="dt-field dt-skeleton mb-5" style={{ width: '50%', height: '220px' }}></div>

                    <div className="dt-field dt-skeleton mb-1" style={{ width: '90%' }}></div>
                    <div className="dt-field dt-skeleton mb-1" style={{ width: '85%' }}></div>
                    <div className="dt-field dt-skeleton mb-1" style={{ width: '90%' }}></div>
                    <div className="dt-field dt-skeleton mb-4" style={{ width: '50%' }}></div>

                    <div className="dt-field dt-skeleton mb-1" style={{ width: '90%' }}></div>
                    <div className="dt-field dt-skeleton mb-1" style={{ width: '85%' }}></div>
                    <div className="dt-field dt-skeleton mb-1" style={{ width: '90%' }}></div>
                    <div className="dt-field dt-skeleton mb-4" style={{ width: '50%' }}></div>

                    <div className="dt-field dt-skeleton mb-1" style={{ width: '90%' }}></div>
                    <div className="dt-field dt-skeleton mb-1" style={{ width: '85%' }}></div>
                    <div className="dt-field dt-skeleton mb-1" style={{ width: '90%' }}></div>
                    <div className="dt-field dt-skeleton" style={{ width: '50%' }}></div>
                </Col>

                <Col sm="12" md="3">
                    <div className="dt-field dt-skeleton w-100 mb-4" style={{ height: '220px' }}></div>
                    <div className="dt-field dt-skeleton w-100 mb-4" style={{ height: '220px' }}></div>
                    <div className="dt-field dt-skeleton w-100" style={{ height: '220px' }}></div>
                </Col>
            </Row>
        );
    }

    const renderModals = () => {
        const {
            datasets,
            pep_tier
        } = resultDetailQuery.data || {};

        const dataSetsToSend = [
            ...(datasets || [])
        ];

        if (pep_tier) {
            dataSetsToSend.push(pep_tier);
        }

        const creationPayload = {
            monitoringId: id,
            supplier: supplierId
        };

        return (
            aiBasedRiskAssessmentModalStatus && (
                <AIRiskAssessmentModal
                    closeModal={() => setAiBasedRiskAssessmentModalStatus(false)}
                    onRisksCreated={() => history.push({ search: '?tab=risk-assessment' })}
                    isOpen={aiBasedRiskAssessmentModalStatus}
                    translateResult={true}
                    aiTranlateRequestOptions={{
                        requestUrl: `${CHAT_BOT_DOMAIN}api/tpdd/risks-associated-translate.ai`,
                        payload: {
                            module: "TPDD_SCREENING_PERSONS",
                            datasets: dataSetsToSend,
                            organizationId: organization.id
                        }
                    }}
                    additionnalDetailsRequestOptions={{
                        requestUrl: `${CHAT_BOT_DOMAIN}api/tpdd/risk-ai-findings.get`,
                        payload: {}
                    }}
                    createRisksRequestOptions={{
                        requestUrl: `${API_BASE_URL}/risk_rate/create_ai`,
                        payload: creationPayload
                    }}>
                    {(result) => {
                        return (
                            <Modal className="ai-risk-result-modal mw-100" style={{ width: '90%' }} isOpen={aiBasedRiskAssessmentModalStatus} size="xl">
                                <ModalHeader toggle={() => setAiBasedRiskAssessmentModalStatus(!aiBasedRiskAssessmentModalStatus)}>
                                    {t('Risk Assesment Support by AI')}
                                </ModalHeader>
                                <ModalBody>
                                    <result.type {...result.props} />
                                </ModalBody>
                            </Modal>
                        )
                    }}
                </AIRiskAssessmentModal>
            )
        )
    }

    const renderContent = () => {
        if (resultDetailQuery.isFetching || fullProfileDetailQuery.isFetching || !resultFullProfileId) {
            return renderLoadingPlaceholder();
        }

        if (resultDetailQuery.isError || fullProfileDetailQuery.isError) {
            return (
                <Alert 
                    color="warning" 
                    className="my-3"
                >
                    <strong>
                        {t('Unable to fetch compliance monitoring result.')}
                    </strong>{' '}
                    {t('The item may not exist, or an unexpected error occurred. Please try again.')}
                </Alert>
            )
        }

        return (
            <>
                <MonitoringBody 
                    setAiBasedRiskAssessmentModalStatus={setAiBasedRiskAssessmentModalStatus}
                    fullProfileDetailQuery={fullProfileDetailQuery}
                    monitoringDetailQuery={resultDetailQuery}
                    t={t}
                />

                {renderModals()}
            </>
        )
    }

    return (
        <div className="finding-details-content">
            {renderContent()}
        </div>
    )
};

const mapStatetoProps = (state) => {
    const { Organization } = state;
    const { token } = state.Login;

    return {
        user: state.Login.user,
        organization: Organization,
        token
    };
};

export default withNamespaces()(
    connect(
        mapStatetoProps,
        {}
    )(MonitoringAlertBody)
);